import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  AlertColor,
  Box,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import FormDialog from "src/components/DialogBox";
import { hasPermission } from 'src/components/Permissions/hasPermission';
import { get } from "src/services/httpClient.service";

const API_URL = process.env.REACT_APP_BASE_API_URL;

interface Module {
  id: string;
  name: string;
  description: string;
  prefix: string;
  sequence: number;
  createdDate: string;
  updatedDate: string;
}

const ModuleApplications = () => {
  const [rows, setRows] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchModulesData();
  }, []);

  const fetchModulesData = async () => {
    try {
      setLoading(true);
      const response = await get(`${API_URL}/api/modules/findAll`);
      
      // FIXED: Access the data correctly based on your response structure
      // The response contains { status, message, responseStatus, statusCode, data: [...] }
      console.log("API Response:", response); // For debugging
      
      // Check if response.data exists and has the data property
      if (response?.data?.data) {
        setRows(response.data.data);
      } 
      // Alternative: if response directly has the data array
      else if (response?.data) {
        setRows(response.data);
      }
      // If response is the array itself
      else if (Array.isArray(response)) {
        setRows(response);
      }
      else {
        setRows([]);
        console.warn("Unexpected response structure:", response);
      }
      
      setError("");
    } catch (err) {
      console.error("Error fetching modules:", err);
      setError("Failed to load modules. Please try again.");
      setSnackbar({ open: true, message: "Failed to load modules.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (module: Module) => {
    setSelectedModule(module);
    setOpenUpdateDialog(true);
  };

  const handleDeleteClick = (module: Module) => {
    setSelectedModule(module);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (!selectedModule) return;

    try {
      await axios.delete(`${API_URL}?moduleId=${selectedModule.id}`);
      setSnackbar({ open: true, message: "Module deleted successfully!", severity: "success" });
      fetchModulesData();
    } catch (err) {
      console.error("Error deleting module:", err);
      setSnackbar({ open: true, message: "Failed to delete the module.", severity: "error" });
    }
  };

  const onClose = () => {
    setOpenDialog(false);
    setOpenUpdateDialog(false);
    setSelectedModule(null);
  };

  const handleAddModuleSubmit = async () => {
    fetchModulesData();
  };

  const handleClick = () => {
     setOpenDialog(true);
  };

  return (
    <Box p={3}>
      <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Module Management
        </Typography>
        {hasPermission(["OWNER", "Admin"]) && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleClick}
          >
            Add Module
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} style={{ marginTop: 20 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box color="red" textAlign="center" padding={4}>
            {error}
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Prefix</strong></TableCell>
                <TableCell><strong>Sequence</strong></TableCell>
                <TableCell><strong>Created Date</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.prefix}</TableCell>
                    <TableCell>{row.sequence}</TableCell>
                    <TableCell>
                      {row.createdDate ? new Date(row.createdDate).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {hasPermission(["OWNER", "Admin"]) && (
                        <>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEditClick(row)}
                            size="small"
                          >
                            <EditIcon sx={{ color: "blue" }} />
                          </IconButton>
                          {/* <IconButton 
                            color="secondary" 
                            onClick={() => handleDeleteClick(row)}
                            size="small"
                          >
                            <DeleteIcon sx={{ color: "red" }} />
                          </IconButton> */}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No modules found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add Module Dialog */}
      {openDialog && (
        <FormDialog
          open={openDialog}
          onClose={onClose}
          title="Add Module"
          component="addModule"
          onSubmit={handleAddModuleSubmit}
          fetchModulesData={fetchModulesData}
        />
      )}

      {/* Edit Module Dialog */}
      {openUpdateDialog && (
        <FormDialog
          open={openUpdateDialog}
          onClose={onClose}
          title="Update Module"
          component="editModule"
          onSubmit={handleAddModuleSubmit}
          fetchModulesData={fetchModulesData}
          module={selectedModule}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the module "{selectedModule?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Success/Error Messages */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleApplications;